/**
 * Scout v0 — QA session summary (P18, §1.2 stage 10).
 *
 * On completion: counts (accepted/rejected), overrides, suppression rules
 * created, and any rules the P06 volume guard held in pending_review —
 * flagged for review. The summary reads scout_feedback_records +
 * scout_suppressions for the run (the same rows persistQaDecisions wrote),
 * so it reflects every decision captured through either interface (CLI or UI).
 */

import { usePromoteSuppression, useQaSummary } from '@/lib/scout-queries';
import { EmptyState, ErrorBanner, LoadingSpinner } from '@/components/ui';
import { getErrorMessage } from '@/lib/queries';
import type { QaSessionSuppression } from '@/types/scout';

function Stat({ label, value, accent }: { label: string; value: number; accent?: string }) {
  return (
    <div className="border border-border rounded-md p-2.5">
      <dt className="text-xs text-muted">{label}</dt>
      <dd className={`text-lg font-semibold ${accent ?? 'text-text'}`}>{value}</dd>
    </div>
  );
}

function SuppressionRow({ rule, runId }: { rule: QaSessionSuppression; runId: string }) {
  const isPending = rule.reviewState === 'pending_review';
  // Per-row mutation instance so each rule carries its own loading/error state.
  const promote = usePromoteSuppression(runId);
  return (
    <tr className="border-b border-border/50">
      <td className="py-2 pr-3 font-mono text-xs text-text">{rule.type}</td>
      <td className="py-2 pr-3 text-text break-all">{rule.value}</td>
      <td className="py-2 pr-3 font-mono text-xs text-muted">{rule.reasonCode}</td>
      <td className="py-2 pr-3 text-muted">{rule.category}</td>
      <td className="py-2 pr-3 text-muted">{rule.scope}</td>
      <td className="py-2 pr-3">
        {isPending ? (
          <div>
            <div className="flex items-center gap-2">
              <span className="badge badge-warning" title="Held by the P06 volume guard - review before activating">
                pending review
              </span>
              {/* §1.6 human-approval gate — only offered while pending_review
                  (the backend 409s any other state). Clicking again after an
                  error is the retry path. */}
              <button
                type="button"
                className="btn-primary text-xs px-2 py-1"
                onClick={() => promote.mutate(rule.suppressionId)}
                disabled={promote.isPending}
              >
                {promote.isPending ? 'Activating…' : 'Activate'}
              </button>
            </div>
            {promote.isError && (
              <p className="text-xs text-warning mt-1" role="alert">
                {getErrorMessage(promote.error)}
              </p>
            )}
          </div>
        ) : (
          <span className="badge badge-primary">active</span>
        )}
      </td>
    </tr>
  );
}

export function QaSessionSummary({ runId }: { runId: string }) {
  const { data: summary, isLoading, error, refetch } = useQaSummary(runId);

  if (error) {
    return (
      <section className="card">
        <ErrorBanner message={getErrorMessage(error)} retryable={error.retryable} onRetry={() => refetch()} />
      </section>
    );
  }

  if (isLoading || !summary) {
    return (
      <section className="card">
        <LoadingSpinner className="py-6" />
      </section>
    );
  }

  if (summary.totalDecisions === 0) {
    return (
      <section className="card">
        <EmptyState
          title="No decisions yet"
          description="Submit QA decisions for this run to see the session summary."
        />
      </section>
    );
  }

  return (
    <div className="space-y-6">
      {/* Guard-flagged rules banner — surfaced when the volume cap held any rule. */}
      {summary.hasGuardFlaggedRules && (
        <section className="card border-warning" aria-labelledby="guard-flagged-heading">
          <div className="flex items-start gap-3">
            <span className="badge badge-warning shrink-0">review</span>
            <div>
              <h2 id="guard-flagged-heading" className="text-base font-semibold text-text">
                {summary.suppressionsPendingReview} rule{summary.suppressionsPendingReview === 1 ? '' : 's'} held by the volume guard
              </h2>
              <p className="text-xs text-muted mt-1">
                The P06 auto-create cap held these deterministic suppression rules in pending_review.
                Review and activate them before the next run so they take effect. Use the Activate
                button on each pending rule below.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Counts */}
      <section className="card" aria-labelledby="qa-summary-heading">
        <h2 id="qa-summary-heading" className="text-base font-semibold text-text mb-3">
          Session summary
        </h2>
        <dl className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
          <Stat label="Total decisions" value={summary.totalDecisions} />
          <Stat label="Accepted" value={summary.accepted} accent="text-primary" />
          <Stat label="Rejected" value={summary.rejected} accent="text-warning" />
          <Stat label="Overrides" value={summary.overrides} accent="text-secondary" />
          <Stat label="Suppressions created" value={summary.suppressionsCreated} />
          <Stat label="Pending review" value={summary.suppressionsPendingReview} accent={summary.hasGuardFlaggedRules ? 'text-warning' : undefined} />
        </dl>
        <p className="text-xs text-muted font-mono">
          {summary.runId} · org {summary.orgId}
        </p>
      </section>

      {/* Suppression rules created this session */}
      {summary.suppressions.length > 0 && (
        <section className="card" aria-labelledby="qa-rules-heading">
          <h3 id="qa-rules-heading" className="text-base font-semibold text-text mb-3">
            Suppression rules created
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted border-b border-border">
                  <th className="py-2 pr-3 font-medium">Type</th>
                  <th className="py-2 pr-3 font-medium">Value</th>
                  <th className="py-2 pr-3 font-medium">Reason</th>
                  <th className="py-2 pr-3 font-medium">Category</th>
                  <th className="py-2 pr-3 font-medium">Scope</th>
                  <th className="py-2 pr-3 font-medium">State</th>
                </tr>
              </thead>
              <tbody>
                {summary.suppressions.map((rule) => (
                  <SuppressionRow key={rule.suppressionId} rule={rule} runId={runId} />
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}